import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ArrowRight, Download, Users, Globe, Shield, CheckCircle } from "lucide-react";
import dashboardImage from "@/assets/demo-dashboard-new-optimized.png";
import renestLogo from "@/assets/renest-logo-transparent.png";

const HeroSection = () => {
  const { t } = useTranslation();

  const scrollToDemo = () => {
    const finalCTA = document.querySelector('#final-cta');
    if (finalCTA) {
      finalCTA.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="pt-16 sm:pt-20 pb-24 sm:pb-32 px-4 bg-gradient-to-br from-background via-primary/5 to-background">
      <div className="container mx-auto">
        {/* Logo Renest */}
        <div className="flex justify-center mb-8 sm:mb-12">
          <img src={renestLogo} alt="Renest Logo" className="h-16 sm:h-24 w-auto" width="192" height="192" />
        </div>
        
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12 items-center">
          {/* Content */}
          <div className="space-y-6 sm:space-y-8">
            <div className="space-y-4 sm:space-y-6">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium">
                <Shield className="h-3 sm:h-4 w-3 sm:w-4" />
                {t('hero.badge')}
              </div>
              
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                {t('hero.title')}{" "}
                <span className="text-primary">{t('hero.titleHighlight')}</span>
              </h1>
              
              <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-lg">
                {t('hero.subtitle')}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg" 
                className="text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 shadow-lg group w-full sm:w-auto" 
                onClick={scrollToDemo}
              >
                {t('hero.cta.demo')}
                <ArrowRight className="ml-2 h-4 sm:h-5 w-4 sm:w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              
              <Button 
                variant="outline" 
                size="lg" 
                className="text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-6 group w-full sm:w-auto" 
                asChild
              >
                <a href="#" onClick={(e) => e.preventDefault()}>
                  <Download className="mr-2 h-4 sm:h-5 w-4 sm:w-5" />
                  {t('hero.cta.download')}
                </a>
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-6 pt-6">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                <span>{t('hero.trust.compliant')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                <span>{t('hero.trust.setup')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                <span>{t('hero.trust.support')}</span>
              </div>
            </div>

            {/* Social proof logos placeholder */}
            <div className="pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground mb-4">{t('hero.trustedBy')}</p>
              <div className="flex items-center gap-8 opacity-50">
                <div className="h-8 w-24 bg-muted rounded"></div>
                <div className="h-8 w-20 bg-muted rounded"></div>
                <div className="h-8 w-28 bg-muted rounded"></div>
                <div className="h-8 w-24 bg-muted rounded hidden sm:block"></div>
              </div>
            </div>
          </div>

          {/* Visual */}
          <div className="relative">
            <div className="relative z-10 bg-white rounded-2xl shadow-2xl border overflow-hidden">
              <img src={dashboardImage} alt="RENEST Dashboard" className="w-full h-auto" width="1188" height="784" />
            </div>
            
            {/* Background decorations */}
            <div className="absolute -top-4 -right-4 w-32 h-32 bg-primary/10 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-8 -left-8 w-40 h-40 bg-primary/5 rounded-full blur-3xl"></div>
            
            {/* Floating elements */}
            <div className="hidden sm:block absolute -top-6 -left-6 bg-white rounded-xl shadow-lg p-4 animate-fade-in z-20">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">10 {t('hero.floatingActive')}</span>
              </div>
            </div>
            
            <div className="hidden sm:block absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg p-4 animate-fade-in z-20" style={{
              animationDelay: "0.5s"
            }}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Globe className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <div className="text-sm font-medium">{t('hero.floatingNewDest')}</div>
                  <div className="text-xs text-muted-foreground">{t('hero.floatingLocation')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
